'use client';

import { useState, memo } from 'react';
import { Button } from '@/components/ui/button';
import { submitFeedback } from '@/lib/api/feedback-api';
import FeedbackSuccessModal from './FeedbackSuccessModal';
import { sanitizeUserInput } from '@/utils/sanitize-input';
import { containsXss } from '@/utils/validate-input';

interface FeedbackTabProps {
  entryUid: string;
  title: string;
  type: 'course' | 'lesson' | 'learning-path';
}

const FeedbackTab = memo(({ entryUid, title, type }: FeedbackTabProps) => {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const maxLength = 2000;

  const handleSubmit = async () => {
    if (!feedback.trim() || isSubmitting) return;

    // Security: Final XSS check before submission
    if (containsXss(feedback)) {
      alert('Invalid feedback content detected. Please remove any scripts or HTML tags.');
      return;
    }

    setIsSubmitting(true);

    const success = await submitFeedback({
      feedback: feedback.trim(),
      entryUid,
      title,
      type,
    });

    setIsSubmitting(false);

    if (success) {
      setFeedback('');
      setShowSuccessModal(true);
    } else {
      // You can add error handling here (toast notification, etc.)
      alert('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <div
      className="flex w-full max-w-full flex-col items-start justify-center md:max-w-[864px]"
      style={{ gap: 'var(--Spacings-space-s)' }}
    >
      {/* Header */}
      <div className="flex w-full flex-row items-center">
        <h3 className="cs-h3 flex-1">Send us your feedback</h3>
      </div>

      {/* Text Box Container */}
      <div className="flex w-full flex-col items-start" style={{ gap: 'var(--Spacings-space-xs)' }}>
        {/* Label */}
        <label htmlFor="feedback-textarea" className="w-full text-[var(--Text-Default-Strong)]">
          Comment/Questions/Suggestions
        </label>

        {/* Textarea Field */}
        <div className="flex w-full flex-col">
          <textarea
            id="feedback-textarea"
            value={feedback}
            onChange={(e) => {
              // Security: Sanitize feedback input to prevent XSS (trim: false so spaces work while typing)
              const sanitized = sanitizeUserInput(e.target.value, false);

              // Security: Block if contains malicious XSS patterns
              if (!containsXss(sanitized) && sanitized.length <= maxLength) {
                setFeedback(sanitized);
              }
            }}
            placeholder="Share your feedback ..."
            className="bg-surface-3 cs-input-value h-[200px] w-full resize-none text-[var(--Text-Default-Light)] placeholder:text-[var(--Text-Default-Light)] focus:ring-1 focus:ring-[var(--Color-Extended-Amethyst-Amethyst-Accent)] focus:outline-none md:h-[250px] lg:h-[292px]"
            style={{
              padding: 'var(--Spacings-space-xs)',
            }}
          />

          {/* Character Count */}
          <div
            className="cs-caption text-right text-[var(--Text-Default-Light)]"
            style={{ marginTop: 'var(--Spacings-space-xs)' }}
          >
            {feedback.length}/{maxLength}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        // disabled={!feedback.trim() || isSubmitting}
        variant="PrimaryFillButton"
        className="w-full md:w-auto"
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </Button>

      {/* Success Modal */}
      <FeedbackSuccessModal open={showSuccessModal} onOpenChange={setShowSuccessModal} />
    </div>
  );
});

FeedbackTab.displayName = 'FeedbackTab';

export default FeedbackTab;
